import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ActionForm } from './ClaimsPage';
import { publicApi } from '@/config/axios';
import { Button } from '@/components/ui/button';
import { SuperbillActions } from './SuperbillActions';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

type Party = { name: string; address: string; city: string; state: string; zip_code: string };
type PatientClaim = {
  form_draft_available: boolean;
  electronic_submission_enabled: boolean;
  billing: {
    patient: Party;
    patient_dob: string;
    provider: Party;
    coverage: { member_id: string; group_number: string; subscriber: Party; relationship: string };
    encounter: {
      rendering_npi: string;
      diagnoses: string[];
      lines: {
        source_line_id: string;
        service_date: string;
        code: string;
        modifiers: string[];
        place_of_service: string;
        units: string;
        charge_cents: number;
        paid_cents: number;
      }[];
    };
  };
  eob: { allowed_cents: number; paid_to_patient_cents: number; deductible_cents: number } | null;
  patient_name: string;
  payer_name: string;
  status: string;
  version: number;
  instructions: string;
  filing_deadline: string | null;
};

export function PatientClaimPage() {
  const [token] = useState(() => window.location.hash.slice(1));
  const [reviewed, setReviewed] = useState(false);
  const [error, setError] = useState('');
  const cache = useQueryClient();
  const [sessionKey] = useState(() => crypto.randomUUID());
  const queryKey = ['patient-claim', sessionKey];
  const config = { headers: { Authorization: `Bearer ${token}` } };
  const base = '/api/v1/claim-portal';
  useEffect(() => {
    window.history.replaceState(null, '', window.location.pathname);
    return () => {
      cache.removeQueries({ queryKey: ['patient-claim', sessionKey] });
    };
  }, [cache, sessionKey]);
  const claim = useQuery<PatientClaim>({
    queryKey,
    gcTime: 0,
    retry: false,
    enabled: !!token,
    queryFn: async () => (await publicApi.get(base, config)).data,
  });
  const mutation = useMutation({
    onSuccess: (value) => {
      cache.setQueryData(queryKey, value);
      setError('');
    },
    onError: () => setError('Unable to save. Check your entries, or reopen your private link if access has expired.'),
    mutationFn: async ({ path, body }: { path: string; body: unknown }) =>
      (await publicApi.post<PatientClaim>(`${base}${path}`, body, config)).data,
  });
  return (
    <main className="mx-auto flex max-w-3xl flex-col gap-6 p-5 md:p-10">
      <header>
        <p className="text-sm text-muted-foreground">MohairNotes · Patient claims</p>
        <h1 className="mt-2 text-2xl font-semibold">Your reimbursement request</h1>
      </header>
      {!token || claim.isError ? (
        <Alert variant="destructive">
          <AlertTitle>Access link unavailable</AlertTitle>
          <AlertDescription>
            Open the private link your practice provided. If it has expired, ask the practice for a new one. Links
            expire after 48 hours.
          </AlertDescription>
        </Alert>
      ) : claim.isLoading ? (
        <p>Loading your claim…</p>
      ) : (
        claim.data && (
          <Card>
            <CardHeader>
              <CardTitle>{claim.data.patient_name}</CardTitle>
              <CardDescription>
                {claim.data.payer_name} · {claim.data.status.replaceAll('_', ' ')}
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-6">
              <Alert>
                <AlertTitle>
                  {claim.data.status === 'reviewed' ? 'Prepare your submission' : 'Submission recorded'}
                </AlertTitle>
                <AlertDescription>
                  {claim.data.status === 'reviewed'
                    ? "Downloading the packet does not submit your claim. Follow the instructions below and keep the insurer's confirmation."
                    : 'Keep your submission confirmation and explanation of benefits. Check with your insurer before submitting this claim again.'}
                </AlertDescription>
              </Alert>
              <SuperbillActions base={base} token={token} />
              <section className="flex flex-col gap-3" aria-label="Review claim details">
                <h2 className="font-medium">1. Review your claim details</h2>
                <p>
                  {claim.data.billing.patient.name} · Birth date {claim.data.billing.patient_dob}
                </p>
                <p>
                  {claim.data.billing.patient.address}, {claim.data.billing.patient.city},{' '}
                  {claim.data.billing.patient.state} {claim.data.billing.patient.zip_code}
                </p>
                <p>
                  Member ID: {claim.data.billing.coverage.member_id} · Group:{' '}
                  {claim.data.billing.coverage.group_number || 'Not supplied'}
                </p>
                <p>
                  Subscriber: {claim.data.billing.coverage.subscriber.name} · Relationship:{' '}
                  {claim.data.billing.coverage.relationship}
                </p>
                <p>
                  Provider: {claim.data.billing.provider.name} · Rendering NPI:{' '}
                  {claim.data.billing.encounter.rendering_npi}
                </p>
                <p>Diagnosis codes: {claim.data.billing.encounter.diagnoses.join(', ')}</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr>
                        {['Date', 'CPT / modifiers', 'Setting (POS)', 'Units', 'Charge', 'Paid'].map((h) => (
                          <th key={h} className="p-2 text-left">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {claim.data.billing.encounter.lines.map((line) => (
                        <tr key={line.source_line_id}>
                          <td className="p-2">{line.service_date}</td>
                          <td className="p-2">{[line.code, ...line.modifiers].join(' ')}</td>
                          <td className="p-2">{line.place_of_service}</td>
                          <td className="p-2">{line.units}</td>
                          <td className="p-2">${(line.charge_cents / 100).toFixed(2)}</td>
                          <td className="p-2">${(line.paid_cents / 100).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p>
                  If anything is incorrect, ask your practice to correct it before you submit. Keep receipts for
                  payments you made.
                </p>
              </section>
              {claim.data.status === 'reviewed' && (
                <section className="flex flex-col gap-3">
                  <h2 className="font-medium">2. Prepare the required forms</h2>
                  <p>
                    The editable CMS-1500 preparation copy includes the available insurance, dates, codes, charges, and
                    payments. It is incomplete and is not signed.
                  </p>
                  <p>
                    Complete names in last/first/middle order, sex, relationship, other insurance and accident
                    questions, applicable authorizations, billing/facility information, tax-ID type, and required
                    signatures with your practice. Confirm whether this form is accepted by your exact plan.
                  </p>
                  <p>
                    Ask the insurer how to request payment to you. Box 13 authorizes payment to the provider; do not
                    sign it automatically when seeking member reimbursement.
                  </p>
                  <Button
                    variant="outline"
                    disabled={!claim.data.form_draft_available}
                    onClick={async () => {
                      try {
                        const response = await publicApi.get(`${base}/form-draft`, { ...config, responseType: 'blob' });
                        const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = 'cms1500-preparation-copy.pdf';
                        link.click();
                        setTimeout(() => URL.revokeObjectURL(url), 1000);
                      } catch {
                        setError('Unable to prepare the form. Ask your practice for assistance.');
                      }
                    }}>
                    Download editable CMS-1500 preparation copy
                  </Button>
                  {!claim.data.form_draft_available && (
                    <p>More than six service lines: ask your practice for continuation forms.</p>
                  )}
                  <a
                    target="_blank"
                    rel="noreferrer"
                    href="https://www.azblue.com/individuals-and-families/resources/forms">
                    AZ Blue members: official claim form and resources
                  </a>
                </section>
              )}
              <div>
                <h2 className="font-medium">3. Submit through your plan’s verified channel</h2>
                <p>
                  Electronic submission from this app is not enabled. Use the practice-verified instructions below. Do
                  not send a duplicate if your provider has already filed this claim.
                </p>
                <p className="mt-2 whitespace-pre-wrap">{claim.data.instructions}</p>
                <p className="mt-3">
                  Filing deadline: {claim.data.filing_deadline || 'Not verified. Confirm with your plan promptly.'}
                </p>
              </div>
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    const result = await publicApi.get(`${base}/packet`, { ...config, responseType: 'blob' });
                    const url = URL.createObjectURL(new Blob([result.data], { type: 'application/pdf' }));
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = 'member-claim-packet.pdf';
                    link.click();
                    setTimeout(() => URL.revokeObjectURL(url), 1000);
                  } catch {
                    setError('Unable to download. Ask your practice for a new link if access has expired.');
                  }
                }}>
                Download claim packet
              </Button>
              {claim.data.status === 'reviewed' && (
                <label className="flex items-start gap-3">
                  <input type="checkbox" checked={reviewed} onChange={(e) => setReviewed(e.target.checked)} />I reviewed
                  these details and already submitted the completed forms through my insurer’s channel. I am recording
                  that submission here.
                </label>
              )}
              {claim.data.status === 'reviewed' && reviewed && (
                <ActionForm
                  kind="submission"
                  busy={mutation.isPending}
                  claim={{ id: 'patient', version: claim.data.version }}
                  onSave={(_path, body) => mutation.mutate({ body, path: '/submission' })}
                />
              )}
              {claim.data.status === 'submitted' && (
                <ActionForm
                  kind="eob"
                  busy={mutation.isPending}
                  claim={{ id: 'patient', version: claim.data.version }}
                  onSave={(_path, body) => mutation.mutate({ body, path: '/eob' })}
                />
              )}
              {claim.data.eob && (
                <p>
                  Reported insurer payment: ${(claim.data.eob.paid_to_patient_cents / 100).toFixed(2)} · Applied to
                  deductible: ${(claim.data.eob.deductible_cents / 100).toFixed(2)}
                </p>
              )}
              {error && (
                <p role="alert" className="text-destructive">
                  {error}
                </p>
              )}
            </CardContent>
          </Card>
        )
      )}
      <p className="text-sm text-muted-foreground">
        Keep this access link private. Reopen the original link to return to this page.
      </p>
    </main>
  );
}
